import * as yup from "yup";
import {
  ValidatedData as GetValidatedData,
  ValidColumnKeysBySheet,
} from "utils/xlsxImportExport/XlsxImportWorkBook";
import createDefinitionHelpers, {
  omitColumns,
} from "utils/xlsxImportExport/definitionHelpers";
import * as orderingPlatformSchemaFields from "utils/schemas/orderingPlatform";
import * as distributionCenterSchemaFields from "utils/schemas/distributionCenter";
import * as addressSchemaFields from "utils/schemas/address";
import * as phoneNumberSchemaFields from "utils/schemas/phoneNumber";
import { UsStateCaProvince } from "__generated__/gql/graphql";

export type CustomerRow = {
  name: string;
  isDefault?: boolean;
};

export type WarehouseRow = {
  customerName: string;
  name: string;
  code?: string | null;
  notes?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: UsStateCaProvince | null;
  addressZip?: string | null;
};

const customersDef = createDefinitionHelpers<CustomerRow>();

export const CUSTOMER_WORKSHEET_DEFINITION = customersDef.workSheet({
  key: "customers",
  sheetName: "Customers",
  schema: yup.object().shape({
    name: orderingPlatformSchemaFields.name,
    isDefault: yup.boolean(),
  }),
  columnDefinitions: [
    { key: "name", header: "Name", isRequired: true },
    { key: "isDefault", header: "Default?" },
  ],
});

export const IMPORT_CUSTOMER_WORKSHEET_DEFINITION = omitColumns(
  CUSTOMER_WORKSHEET_DEFINITION,
  ["isDefault"],
);

const warehousesDef = createDefinitionHelpers<WarehouseRow>();

const WAREHOUSE_WORKSHEET_DEFINITION = warehousesDef.workSheet({
  key: "warehouses",
  sheetName: "Warehouses",
  schema: yup.object().shape({
    customerName: orderingPlatformSchemaFields.name,
    name: distributionCenterSchemaFields.name,
    code: distributionCenterSchemaFields.code,
    notes: distributionCenterSchemaFields.notes,
    phone: phoneNumberSchemaFields.phone.transform(
      phoneNumberSchemaFields.coercePhoneTransform,
    ),
    addressLine1: addressSchemaFields.line1,
    addressLine2: addressSchemaFields.line2,
    addressCity: addressSchemaFields.city,
    addressState: addressSchemaFields.state,
    addressZip: addressSchemaFields.zip,
  }),
  columnDefinitions: [
    { key: "customerName", header: "Customer Name", isRequired: true },
    { key: "name", header: "Name", isRequired: true },
    { key: "code", header: "Code" },
    { key: "notes", header: "Notes" },
    { key: "phone", header: "Phone" },
    { key: "addressLine1", header: "Address Line 1" },
    { key: "addressLine2", header: "Address Line 2" },
    { key: "addressCity", header: "City" },
    { key: "addressState", header: "State" },
    { key: "addressZip", header: "ZIP Code" },
  ],
});

export const WORKSHEET_DEFINITIONS = [
  CUSTOMER_WORKSHEET_DEFINITION,
  WAREHOUSE_WORKSHEET_DEFINITION,
];

export const IMPORT_WORKSHEET_DEFINITIONS = [
  IMPORT_CUSTOMER_WORKSHEET_DEFINITION,
  WAREHOUSE_WORKSHEET_DEFINITION,
];

export type WorkSheetDefinitions = (typeof WORKSHEET_DEFINITIONS)[number];
export type ImportWorkSheetDefinitions =
  (typeof IMPORT_WORKSHEET_DEFINITIONS)[number];

export type ValidatedData = GetValidatedData<WorkSheetDefinitions>;
export type ImportValidatedData = GetValidatedData<ImportWorkSheetDefinitions>;

export type ValidColumnKeys = ValidColumnKeysBySheet<WorkSheetDefinitions>;
export type ImportValidColumnKeys =
  ValidColumnKeysBySheet<ImportWorkSheetDefinitions>;
